import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const glew: Competitor = {
  "id": "glew",
  "name": "Glew.io",
  "group": "ecomm",
  "website": "https://www.glew.io/",
  "owner": "Everest Group LLC, an Omaha-based private investment firm (acquisition announced March 2026)",
  "summary": "Glew is an ecommerce analytics and BI platform that pulls store, marketplace, marketing and ERP data into prebuilt dashboards, with a higher tier that adds a managed data warehouse, ELT and bundled Looker reporting. It targets multi-channel retailers and agencies that want reporting without building their own stack.",
  "bestFor": [
    "Multi-channel brands (Shopify/BigCommerce plus Amazon, TikTok Shop and POS) that want prebuilt sales, product, customer and subscription dashboards quickly",
    "Small teams on a budget where Glew Pro at about $250/month covers core reporting",
    "Brands that want a vendor-managed warehouse plus a bundled Looker license instead of buying BI separately (Glew Plus)"
  ],
  "notFor": [
    "Brands that need pixel-based multi-touch attribution, MMM or incrementality testing",
    "Teams that want all modeled data inside their own Snowflake or BigQuery account under their own dbt project",
    "Buyers who need clear, published pricing for the warehouse tier"
  ],
  "strengths": [
    "Large prebuilt connector catalog (Glew cites 170+ sources on Plus) covering Shopify, Amazon Seller and Vendor Central, TikTok Shop, NetSuite, Lightspeed and Recharge, so no one has to build and maintain those pipelines",
    "Hundreds of out-of-the-box dashboards and 250+ predefined KPIs on day one, versus weeks of dbt modeling and Sigma workbook building in a custom project",
    "Entry tier at about $250/month is far below the cost of a consultant-built Snowflake + dbt + Sigma stack plus warehouse compute",
    "Plus bundles ELT, a managed warehouse and a Looker license in one contract, so no separate BI or warehouse licenses are needed",
    "Ready-made subscription metrics (MRR, churn, subscriber and plan LTV) and product cost management with CSV import or platform sync"
  ],
  "limitations": [
    "Glew's own pricing page lists a data warehouse, managed ETL and Looker only on Glew Plus; Glew Pro is limited to about 40 integrations",
    "Glew's FAQ says custom SQL queries require Glew Plus or Enterprise",
    "The Glew Plus warehouse is Glew's own Amazon Redshift environment; its docs do not describe loading data into a customer-owned Snowflake or BigQuery account",
    "Attribution is documented as first-order or last-click (plus Facebook's reported attribution); no first-party pixel, MMM or incrementality is described",
    "Glew Plus pricing is not published and depends on the business and its data and integrations, per Glew's FAQ",
    "Integration counts on Glew's own pages are inconsistent (40, 150+, 170+, 180+), so coverage should be confirmed in a demo"
  ],
  "pricing": {
    "model": "Flat monthly fee for Pro; custom quote for Plus",
    "publicStartingPrice": "Glew Pro $250/month, annual prepayment required",
    "freeTier": "No free tier on the current pricing page; Glew Pro offers a free trial (length not stated). An older product update mentions a free 'Glew Starter' product, but it is not on the current pricing page.",
    "notes": "Two tiers listed: Glew Pro ($250/month billed annually; core analytics, about 40 integrations, 250+ KPIs, report builder, scheduled reports, Klaviyo/Mailchimp segment sync, daily email snapshot) and Glew Plus (custom pricing; adds data warehouse, fully managed ETL, bundled Looker license with prebuilt LookML, 150-180+ integrations). Glew's FAQ says Plus/Enterprise cost depends on the business and its data and integrations. Setup: Glew says a Shopify store connects in minutes and initial imports from some sources can take from a few hours to 72+ hours; most data refreshes hourly, customer and inventory data nightly.",
    "sourceUrl": "https://www.glew.io/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Connects via Shopify login; blends with GA, Amazon, ads and Recharge",
      "sourceUrl": "https://glew.io/integrations/shopify"
    },
    "amazon": {
      "value": "yes",
      "note": "Amazon Seller Central and Vendor Central integrations (NA, EU, Far East marketplaces)",
      "sourceUrl": "https://www.glew.io/articles/glew-commerce-data-cloud-and-amazon-vendor-seller-central-integrations-unleash-insights-for-global-brands"
    },
    "tiktok_shop": {
      "value": "yes",
      "note": "TikTok Shop integration announced Dec 2024; plan tier not stated",
      "sourceUrl": "https://www.glew.io/articles/introducing-glews-tiktok-shop-integration-unified-analytics-for-smarter-social-commerce"
    },
    "wholesale_retail_or_erp": {
      "value": "yes",
      "note": "NetSuite, Dynamics 365, Lightspeed Retail POS, NuOrder, Cin7, Fishbowl, Linnworks listed; likely Plus-tier (Pro has ~40 integrations)",
      "sourceUrl": "https://www.glew.io/integrations"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Google Ads, Facebook Ads, Pinterest, Bing, Amazon Ads listed; TikTok Ads not clearly confirmed",
      "sourceUrl": "https://www.glew.io/integrations"
    },
    "first_party_pixel_attribution": {
      "value": "no",
      "note": "Docs describe first-order and last-click attribution only; no own tracking pixel described",
      "sourceUrl": "https://www.glew.io/faqs"
    },
    "mmm_incrementality": {
      "value": "unknown",
      "note": "Not mentioned on Glew's product or platform pages",
      "sourceUrl": "https://www.glew.io/platform-overview"
    },
    "profit_cogs": {
      "value": "yes",
      "note": "Cost Manager, CSV import or platform-synced product costs feed profit reporting",
      "sourceUrl": "https://www.glew.io/faqs"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "LTV by channel; repeat-purchase and LTV cohort reporting in Glew Plus",
      "sourceUrl": "https://www.glew.io/videos/customer-cohort-reporting"
    },
    "subscription_analytics": {
      "value": "yes",
      "note": "Recharge integration with MRR, churn, retention and subscriber/plan LTV",
      "sourceUrl": "https://www.glew.io/integrations/recharge-analytics-reporting"
    },
    "warehouse_access": {
      "value": "partial",
      "note": "Glew Plus only: hosted Redshift warehouse queryable by your BI tool; export to your own Snowflake/BigQuery not documented",
      "sourceUrl": "https://www.glew.io/solutions/glew-plus"
    },
    "custom_metrics": {
      "value": "partial",
      "note": "Custom reports and custom measures via bundled Looker on Plus; Pro limited to report builder on predefined KPIs",
      "sourceUrl": "https://www.glew.io/pricing"
    },
    "custom_sql": {
      "value": "partial",
      "note": "Custom SQL queries require Glew Plus or Enterprise",
      "sourceUrl": "https://www.glew.io/faqs"
    },
    "ai_assistant": {
      "value": "unknown",
      "note": "Site headline mentions AI but no specific AI assistant feature is documented",
      "sourceUrl": "https://www.glew.io/"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "No mobile app found; daily email snapshot is offered for on-the-go use",
      "sourceUrl": "https://www.glew.io/"
    }
  },
  "recentChanges": [
    {
      "date": "2026-03",
      "change": "Everest Group LLC acquired Glew.io; IT ExchangeNet advised Glew",
      "sourceUrl": "https://www.pulse.bot/ai/deals/everest-group-acquires-commerce-data-and-ai-platform-glewio-cf73f698-1d26-4920-aeb2-a07c04ef4ff8"
    },
    {
      "date": "2026",
      "change": "Pricing page now shows Glew Pro at a published $250/month (annual prepay) and Glew Plus as custom; positioning shifted to 'Commerce Data + AI'",
      "sourceUrl": "https://www.glew.io/pricing"
    },
    {
      "date": "2024-12",
      "change": "Added TikTok Shop integration",
      "sourceUrl": "https://www.glew.io/articles/introducing-glews-tiktok-shop-integration-unified-analytics-for-smarter-social-commerce"
    }
  ],
  "sources": [
    {
      "title": "Glew Pricing",
      "url": "https://www.glew.io/pricing"
    },
    {
      "title": "Glew home page",
      "url": "https://www.glew.io/"
    },
    {
      "title": "Glew FAQs",
      "url": "https://www.glew.io/faqs"
    },
    {
      "title": "Glew Plus",
      "url": "https://www.glew.io/solutions/glew-plus"
    },
    {
      "title": "Glew Platform Overview",
      "url": "https://www.glew.io/platform-overview"
    },
    {
      "title": "Glew Data Warehouse",
      "url": "https://www.glew.io/products/data-warehouse"
    },
    {
      "title": "Glew Integrations",
      "url": "https://www.glew.io/integrations"
    },
    {
      "title": "Glew Shopify integration",
      "url": "https://glew.io/integrations/shopify"
    },
    {
      "title": "Glew + Recharge",
      "url": "https://www.glew.io/integrations/recharge-analytics-reporting"
    },
    {
      "title": "Glew Amazon Seller & Vendor Central integrations",
      "url": "https://www.glew.io/articles/glew-commerce-data-cloud-and-amazon-vendor-seller-central-integrations-unleash-insights-for-global-brands"
    },
    {
      "title": "Glew TikTok Shop integration",
      "url": "https://www.glew.io/articles/introducing-glews-tiktok-shop-integration-unified-analytics-for-smarter-social-commerce"
    },
    {
      "title": "Glew customer cohort reporting webinar",
      "url": "https://www.glew.io/videos/customer-cohort-reporting"
    },
    {
      "title": "Glew product updates",
      "url": "https://www.glew.io/product-updates"
    },
    {
      "title": "Everest Group acquires Glew.io (pulse.bot)",
      "url": "https://www.pulse.bot/ai/deals/everest-group-acquires-commerce-data-and-ai-platform-glewio-cf73f698-1d26-4920-aeb2-a07c04ef4ff8"
    },
    {
      "title": "Everest Group acquires Glew.io (SalesTechStar)",
      "url": "https://salestechstar.com/?p=259751"
    }
  ],
  "lastReviewed": "2026-11-03"
};
